import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { ApprovalService } from '../approval.service';
import { ApprovalProcessLogRow } from '../approval.model';

/** The three sequential gates a department row moves through. */
type GateKey = 'reviewed' | 'approved' | 'shared';

/**
 * Approval Dashboard.
 *
 * Lists the Livestock Bordereau review/approval process log rows that have been
 * reviewed (isReviewed = 1), newest reporting period first. Each row shows its
 * three gates — Reviewed, Approved, Shared — with the actor and timestamp the
 * table records for each.
 */
@Component({
  selector: 'app-approval-dashboard',
  standalone: false,
  templateUrl: './approval-dashboard.component.html',
  styleUrl: './approval-dashboard.component.scss'
})
export class ApprovalDashboardComponent implements OnInit {
  rows: ApprovalProcessLogRow[] = [];

  loading = false;
  error: string | null = null;

  constructor(
    private readonly service: ApprovalService,
    private readonly title: Title
  ) {}

  ngOnInit(): void {
    this.title.setTitle('Approval Dashboard');
    this.load();
  }

  /** Loads the reviewed process-log rows. */
  load(): void {
    this.loading = true;
    this.error = null;
    this.service.getReviewedProcessLogs().subscribe({
      next: (rows) => {
        this.rows = rows;
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.error = 'Could not load the approval process log. Please try again.';
      }
    });
  }

  /** Whether the given gate is set on the row. */
  isDone(row: ApprovalProcessLogRow, gate: GateKey): boolean {
    if (gate === 'reviewed') {
      return row.isreviewed === true;
    }
    if (gate === 'approved') {
      return row.isapproved === true;
    }
    return row.isshared === true;
  }

  /** Label for a gate's state — the pill text. */
  gateLabel(row: ApprovalProcessLogRow, gate: GateKey): string {
    if (!this.isDone(row, gate)) {
      return 'Pending';
    }
    return gate === 'reviewed' ? 'Reviewed' : gate === 'approved' ? 'Approved' : 'Shared';
  }

  /**
   * The "who and when" line under a gate's pill, e.g.
   * `Josh McFarlane · 8/4/2026, 9:30 AM`. Empty when the gate isn't set.
   */
  gateMeta(row: ApprovalProcessLogRow, gate: GateKey): string {
    if (!this.isDone(row, gate)) {
      return '';
    }
    const by =
      gate === 'reviewed' ? row.reviewedby : gate === 'approved' ? row.approvedby : row.sharedby;
    const on =
      gate === 'reviewed' ? row.reviewdate : gate === 'approved' ? row.approveddate : row.shareddate;
    return [by?.trim(), this.formatDateTime(on)].filter((part) => !!part).join(' · ');
  }

  /** Formats an ISO `yyyy-MM-dd` reporting period as e.g. "Jul 31, 2026". */
  formatPeriod(raw: string | null): string {
    if (!raw) {
      return '';
    }
    // Parse as local midnight so the display doesn't shift a day in negative time zones.
    const date = new Date(`${raw.substring(0, 10)}T00:00:00`);
    return isNaN(date.getTime())
      ? raw
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /** Formats a timestamp as a US short date and time, or '' when absent/invalid. */
  formatDateTime(raw: string | null): string {
    if (!raw) {
      return '';
    }
    const date = new Date(raw);
    return isNaN(date.getTime())
      ? ''
      : date.toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
  }

  /** Lowercased department, used to tint the department pill. */
  departmentClass(row: ApprovalProcessLogRow): string {
    return (row.department ?? '').trim().toLowerCase();
  }

  /** Stable key for the table's row tracking. */
  trackRow(_index: number, row: ApprovalProcessLogRow): number | string {
    return row.reviewid ?? `${row.department}-${row.reportingperiod}`;
  }
}
